import { useState } from 'react';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import { toasty, FormValidated, Input } from '../../common/forms/FormElements';
import { BlockBorder } from '../../common/Block';
import { SaveButton } from '../../common/forms/SaveButton';

const describeError = (err) => err?.handledError?.message
    || err?.message
    || 'The request failed.';

/**
 * Support tool: send a one-line test SMS or email through this web app's own Twilio / SendGrid
 * configuration. The body is "Test from <host>" so the recipient can tell which environment sent
 * it. Standalone: no AdminTool row, no execution history — the result line is the record.
 */
export const TestMessagingForm = () => {
    const [smsValidated, setSmsValidated] = useState(false);
    const [emailValidated, setEmailValidated] = useState(false);
    const [sending, setSending] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    // One send at a time: the result line and the toast are shared, so two in-flight sends
    // would overwrite each other's outcome. Each form disables its own button while saving;
    // `sending` disables the other form's button too.
    const send = async (channel, route, payload, describeSuccess) => {
        setSending(true);
        try {
            const result = await httpClient.post(route, payload);
            if (!result) throw new Error('No response from the server.');
            setLastResult(describeSuccess(result));
            toasty.success(`Test ${channel} sent.`);
        } catch (err) {
            setLastResult(`${channel} failed: ${describeError(err)}`);
            toasty.error(`Test ${channel} failed. See the result below.`);
        } finally {
            setSending(false);
        }
    };

    const sendSms = ({ phoneNumber }) => send(
        'SMS',
        ApiRoutes.adminTool.testSms(),
        { phoneNumber },
        (r) => `SMS ${r.status} to ${r.to} from ${r.from} (${r.sid})`,
    );

    const sendEmail = ({ email }) => send(
        'email',
        ApiRoutes.adminTool.testEmail(),
        { email },
        (r) => `Email sent to ${r.to}${r.redirected ? ' (redirected by SMTP:OverrideSendToEmail)' : ''} from ${r.from}, subject "${r.subject}"`,
    );

    return (
        <div>
            <FormValidated
                id="testSmsForm"
                name="testSmsForm"
                initialValues={{ phoneNumber: '' }}
                isValidated={smsValidated}
                setIsValidated={setSmsValidated}
                onSubmit={sendSms}
            >
                {() => (
                    <>
                        <Input
                            type="tel"
                            label="Phone number"
                            id="testSmsPhoneNumber"
                            name="phoneNumber"
                            placeholder="+1 555 555 5555"
                            required
                            error="A phone number is required"
                        />
                        <div className="d-flex justify-content-center">
                            <SaveButton
                                savingLabel="Sending"
                                disabled={sending}
                                label="Send SMS"
                            />
                        </div>
                    </>
                )}
            </FormValidated>

            <BlockBorder />

            <FormValidated
                id="testEmailForm"
                name="testEmailForm"
                initialValues={{ email: '' }}
                isValidated={emailValidated}
                setIsValidated={setEmailValidated}
                onSubmit={sendEmail}
            >
                {() => (
                    <>
                        <Input
                            type="email"
                            label="Email address"
                            id="testEmailAddress"
                            name="email"
                            placeholder="someone@example.com"
                            required
                            error="A valid email address is required"
                        />
                        <div className="d-flex justify-content-center">
                            <SaveButton
                                savingLabel="Sending"
                                disabled={sending}
                                label="Send Email"
                            />
                        </div>
                    </>
                )}
            </FormValidated>

            {lastResult && (
                <>
                    <BlockBorder />
                    <p
                        className="mb-0 text-monospace small"
                        data-testid="test-messaging-result"
                    >
                        {lastResult}
                    </p>
                </>
            )}
        </div>
    );
};
